import { MIME_TYPES } from '@mantine/dropzone'
import { ChangeEvent, DragEvent, useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'

import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { AcknowledgeModal, ActionModal, Alert, ButtonText, Dropzone, Modal, Spacer, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'

export function CsvUploadModal({
  isOpen,
  setIsOpen,
  refresh
}: {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  refresh: () => void
}) {
  const { autoRejectionListService } = useServices()
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [hasTooManyFilesError, setHasTooManyFilesError] = useState(false)
  const { t } = useTranslation()

  const onClose = useCallback(() => {
    setIsOpen(false)
    setSelectedFiles([])
    setHasTooManyFilesError(false)
  }, [setIsOpen, setSelectedFiles])

  const [{ status, data, error }, { apiFunc }] = useMutation(autoRejectionListService.uploadAutoRejectionList)
  const handleSubmit = useCallback(async () => {
    const csv = await selectedFiles[0].text()
    apiFunc(csv)
    refresh()
  }, [selectedFiles, apiFunc, refresh])

  const handleFileChange = useCallback((files: File[]) => {
    setSelectedFiles(files)
    setHasTooManyFilesError(false)
  }, [])

  const handleFileInputChange = useCallback((event: ChangeEvent<HTMLDivElement>) => {
    const target = event.target as HTMLInputElement | null

    if (target?.type !== 'file') return

    const fileCount = target.files?.length ?? 0
    setHasTooManyFilesError(fileCount > 1)
  }, [])

  const handleDropCapture = useCallback((event: DragEvent<HTMLDivElement>) => {
    const fileCount = event.dataTransfer?.files?.length ?? 0
    setHasTooManyFilesError(fileCount > 1)
  }, [])

  const handleCopyErrors = useCallback(() => {
    if (!data?.data.errors) return

    window.navigator.clipboard.writeText(data.data.errors.map(err => `${err.field}: ${err.message}`).join('\n'))
  }, [data])

  const actionModal = useMemo(
    () => (
      <ActionModal
        title={t('dialog.csvUpload.title')}
        primaryButtonText={t('button.uploadList')}
        primaryBtnTestId="upload-csv-submit-button"
        secondaryButtonText={t('button.cancel')}
        handleClose={onClose}
        handleSubmit={handleSubmit}
        loading={status === 'pending'}
        disabled={selectedFiles.length === 0 || hasTooManyFilesError || status === 'pending'}
      >
        <Alert variant="info">
          <strong>{t('dialog.csvUpload.alert.title')}</strong>
          <div>{t('dialog.csvUpload.alert.description')}</div>
          <ul style={{ paddingLeft: 16 }}>
            <li>
              <Trans i18nKey="dialog.csvUpload.alert.columns.email" components={{ b: <strong />, i: <i /> }} />
            </li>
            <li>
              <Trans
                i18nKey="dialog.csvUpload.alert.columns.rejectionReason"
                components={{ b: <strong />, i: <i /> }}
              />
            </li>
          </ul>
        </Alert>
        <Spacer size={4} />
        <div
          data-testid="upload-csv-dropzone-container"
          onChangeCapture={handleFileInputChange}
          onDropCapture={handleDropCapture}
        >
          <Dropzone
            title={t('grants.programDetails.taskTemplates.preview.dropzoneTitle')}
            description={t('dialog.csvUpload.preview.dropzoneDescription')}
            files={selectedFiles}
            maxFiles={1}
            onChange={handleFileChange}
            accept={[MIME_TYPES.csv]}
            maxSize={2 * 1024 * 1024}
            testId="upload-csv-dropzone"
          />
        </div>
      </ActionModal>
    ),
    [
      t,
      onClose,
      handleSubmit,
      selectedFiles,
      status,
      hasTooManyFilesError,
      handleFileChange,
      handleFileInputChange,
      handleDropCapture
    ]
  )

  const successModal = useMemo(() => {
    if (!data?.data.errors || data.data.errors.length === 0) {
      return (
        <AcknowledgeModal
          result="positive"
          title={t('dialog.csvUpload.success.title')}
          description={t('dialog.csvUpload.success.description')}
          buttonText={t('button.close')}
          handleClose={onClose}
        />
      )
    }

    return (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.csvUpload.partialSuccess.title')}
        buttonText={t('button.close')}
        handleClose={onClose}
      >
        <Text size="small" align="center">
          {t('dialog.csvUpload.partialSuccess.description', {
            success: data?.data.created,
            failed: data?.data.failed
          })}{' '}
          <span style={{ display: 'inline-flex' }}>
            <ButtonText variant="primary" onClick={handleCopyErrors}>
              {t('dialog.csvUpload.partialSuccess.viewList')}
            </ButtonText>
          </span>
        </Text>
      </AcknowledgeModal>
    )
  }, [data, t, onClose, handleCopyErrors])

  const errorModal = useMemo(() => {
    const errorCode = (error as AxiosError<{ error: { code: string } }>)?.response?.data?.error?.code
    type CsvUploadErrorMessageKey =
      | 'dialog.csvUpload.error.messages.missingRequiredCsvHeaders'
      | 'dialog.csvUpload.error.messages.maxCsvRowsExceeded'
      | 'dialog.csvUpload.error.messages.invalidRecords'
      | 'dialog.csvUpload.error.messages.unknown'
    const errorMessageKeyByCode: Record<string, CsvUploadErrorMessageKey> = {
      'agent_verification_auto_rejection_list/bulk_upload/missing_required_csv_headers':
        'dialog.csvUpload.error.messages.missingRequiredCsvHeaders',
      'agent_verification_auto_rejection_list/bulk_upload/max_csv_rows_exceeded':
        'dialog.csvUpload.error.messages.maxCsvRowsExceeded',
      'agent_verification_auto_rejection_list/bulk_upload/invalid_records':
        'dialog.csvUpload.error.messages.invalidRecords'
    }
    const errorMessageKey = errorCode
      ? errorMessageKeyByCode[errorCode] ?? 'dialog.csvUpload.error.messages.unknown'
      : 'dialog.csvUpload.error.messages.unknown'

    return (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.csvUpload.error.title')}
        description={
          <div>
            <p>{t('dialog.csvUpload.error.description')}</p>
            <p>{t(errorMessageKey)}</p>
          </div>
        }
        buttonText={t('button.close')}
        handleClose={onClose}
      />
    )
  }, [t, error, onClose])

  return (
    <Modal open={isOpen} onClose={onClose}>
      {status !== 'success' && status !== 'error' && actionModal}
      {status === 'success' && successModal}
      {status === 'error' && errorModal}
    </Modal>
  )
}
